<template>
    <div
        class="filters-list"
        :class="{
            open,
        }"
    >
        <button
            class="toggle"
            @click="() => open = !open"
        >
            <DSFontIcon code="filter" icon-style="line" size="l" />
            <span class="text">{{ $t('filters') }}</span>
            <span v-if="count.filtered > 0" class="count">{{ count.filtered }}</span>
        </button>
        <div class="detailed-filters">
            <div class="heading">
                <h3 class="h5">{{ $t('titles.filtering') }}</h3>
                <span class="count">{{ count.filtered }} / {{ count.total }}</span>
            </div>
            <div
                v-for="{ filters: filterGroup }, key in filters"
                :key="key"
                class="filter-group"
            >
                <h4 class="h6">{{ key }}</h4>
                <button
                    v-for="button, bKey in filterGroup"
                    :key="`button-${key}-${bKey}`"
                    class="ds-btn filter"
                    :class="{
                        'selected': selected.includes(button.code),
                    }"
                    @click="$emit('select', button.code)"
                >
                    <DSFontIcon v-if="button.icon" size="xl" :code="button.icon" />
                    <span class="title">{{ button.label }}</span>
                </button>
            </div>
        </div>
        <div class="mobile-background" @click="() => open = !open">
            <button class="close">
                <DSFontIcon code="chevron-left" icon-set="st1website" />
            </button>
        </div>
    </div>
</template>
<script setup>
import { useI18n } from '#imports';
import { ref } from 'vue';

const open = ref(false);

defineProps({
    selected: {
        type: Array,
        required: false,
        default: () => [],
    },
    count: {
        type: Object,
        required: false,
        default: () => ({
            filtered: 0,
            total: 0,
        }),
    },
    filters: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

defineEmits(['select']);

useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
        },
        'fi-FI': {
            searchStation: 'Hae asemaa',
            filters: 'Suodatin',
            titles: {
                filtering: 'Suodata asemia',
                stationTypes: 'Asematyypit',
                food: 'Ruoka',
                services: 'Palvelut',
                fuels: 'Polttoaineet',
                otherServices: 'Muut palvelut',
            },
        },
        'sv-SE': {
            filters: 'Filter',
            titles: {
                filtering: 'Filtrera stationer',
            },
        },
        'no-NO': {
            filters: 'Filter',
            titles: {
                filtering: 'Filter',
            },
        },
    },
});
</script>
<style scoped>
.filters-list {
  transition: all 0.2s ease-in-out;
  position: relative;
  z-index: 2;
}
@media (max-width: 1199px) {
  .filters-list {
    pointer-events: none;
  }
  .filters-list.open .mobile-background {
    opacity: 1;
    pointer-events: auto;
  }
  .filters-list.open .detailed-filters {
    transform: translateX(0);
  }
}

.detailed-filters {
  transition: all 0.5s ease;
  padding: 1.5rem 0.75rem 0.75rem;
  pointer-events: auto;
}
@media (max-width: 1199px) {
  .detailed-filters {
    transform: translateX(-100%);
    position: absolute;
    z-index: 4;
    left: 0;
    top: 0;
    max-height: 100%;
    height: 100%;
    max-width: calc(100% - 4rem);
    background: var(--color-base-white);
    overflow-x: scroll;
  }
}

.count {
  display: inline-flex;
  align-self: center;
  width: auto;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.5rem;
  background: var(--color-warning-500);
  padding: 0 0.375rem;
  border-radius: 0.25rem;
  transform-origin: center center;
  transform: scale(1);
}

.heading {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
}
.heading .h5 {
  margin-bottom: 0;
}

.toggle {
  z-index: 0;
  position: absolute;
  left: 0.5rem;
  top: 12.5rem;
  border: 1px solid var(--color-base-grey-400);
  display: inline-flex;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  background: var(--color-base-white);
  -webkit-tap-highlight-color: transparent;
}
.toggle .icon {
  margin-right: 0.5rem;
}
.toggle .text {
  font-size: 0.875rem;
  font-weight: 800;
}
@media (min-width: 1200px) {
  .toggle {
    display: none;
  }
}

.filter-group {
  margin-bottom: -0.5rem;
  display: flex;
  flex-flow: row wrap;
}
.filter-group .h6 {
  display: flex;
  flex-grow: 1;
  width: 100%;
  margin-top: 1rem;
}
.filter-group .ds-btn.filter {
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  padding-inline: 0.5rem;
  padding-block: 0.25rem;
  gap: 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 0.25rem;
  border: 1px solid var(--color-base-grey-200);
  margin-bottom: 0.5rem;
  margin-right: 0.25rem;
  min-height: 1.75rem;
}
@media (min-width: 1200px) {
  .filter-group .ds-btn.filter:hover {
    border-color: var(--color-primary-100);
    background: var(--color-primary-100);
    color: var(--color-base-black);
  }
}
.filter-group .ds-btn.filter.selected {
  border-color: var(--color-secondary-500);
  background: var(--color-secondary-500);
}

.mobile-background {
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  position: relative;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(0.25rem);
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
}
@media (min-width: 1200px) {
  .mobile-background {
    display: none;
  }
}
.mobile-background .close {
  margin-top: 1rem;
  margin-right: 1rem;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  pointer-events: none;
  background: var(--color-base-white);
}
</style>
